import { apiClient } from './axios';

export interface RewardsBalanceResponse {
  balance: number;
  uiBalance: string;
  decimals: number;
  walletAddress: string | null;
  tokenMint: string;
  chain: string;
  claimableRewards: number;
  message?: string;
}

export interface CreateWalletResponse {
  walletAddress: string;
  chain: string;
  message: string;
  created: boolean;
}

export interface ClaimRewardsResponse {
  success: boolean;
  claimedAmount: number;
  transactionSignature: string;
  walletAddress: string;
  message: string;
}

export interface TransferToWalletRequest {
  destinationAddress: string;
  amount: number;
}

export interface TransferToWalletResponse {
  success: boolean;
  transferredAmount: number;
  transactionSignature: string;
  fromWallet: string;
  toWallet: string;
  message: string;
}

export async function getRewardsBalance(): Promise<RewardsBalanceResponse> {
  const response =
    await apiClient.get<RewardsBalanceResponse>('/rewards/balance');
  return response.data;
}

export async function createWallet(): Promise<CreateWalletResponse> {
  const response =
    await apiClient.post<CreateWalletResponse>('/rewards/wallet');
  return response.data;
}

export async function claimRewards(): Promise<ClaimRewardsResponse> {
  const response = await apiClient.post<ClaimRewardsResponse>('/rewards/claim');
  return response.data;
}

export async function transferToWallet(
  request: TransferToWalletRequest
): Promise<TransferToWalletResponse> {
  const response = await apiClient.post<TransferToWalletResponse>(
    '/rewards/transfer',
    request
  );
  return response.data;
}

// Earning Actions API
export interface EarningAction {
  id: string;
  actionKey: string;
  name: string;
  description?: string;
  points: number;
  icon: string;
  userType: 'owner' | 'professional';
  enabled: boolean;
  cap?: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  link?: string | null;
  isAutoReward?: boolean;
  finished?: boolean;
  submitted?: boolean;
  submissionStatus?: 'pending' | 'approved' | 'rejected' | null;
}

export interface GetEarningActionsResponse {
  earningActions: EarningAction[];
  total: number;
}

export interface CreateEarningActionRequest {
  actionKey: string;
  name: string;
  description?: string;
  points: number;
  icon: string;
  userType: 'owner' | 'professional';
  enabled?: boolean;
  cap?: number | null;
  sortOrder?: number;
}

export interface UpdateEarningActionRequest {
  name?: string;
  description?: string;
  points?: number;
  icon?: string;
  enabled?: boolean;
  cap?: number | null;
  sortOrder?: number;
}

export async function getEarningActions(
  userType?: 'owner' | 'professional'
): Promise<GetEarningActionsResponse> {
  const params = userType ? { userType } : {};
  const response = await apiClient.get<GetEarningActionsResponse>(
    '/rewards/earning-actions',
    { params }
  );
  return response.data;
}

// Submit an earning action for admin review (for actions with URLs)
export interface SubmitEarningActionRequest {
  actionKey: string;
  actionName: string;
  reward: number;
  evidence?: string;
}

export interface SubmitEarningActionResponse {
  submission?: {
    id: string;
    actionKey: string;
    status: string;
    submittedAt: string;
  };
  error?: string;
}

export async function submitEarningAction(
  request: SubmitEarningActionRequest
): Promise<SubmitEarningActionResponse> {
  const response = await apiClient.post<SubmitEarningActionResponse>(
    '/rewards/earning-actions/submit',
    request
  );
  return response.data;
}

// Complete an earning action directly (for actions without URLs — auto-complete)
export async function completeEarningAction(
  actionKey: string
): Promise<{ finished: boolean; actionKey: string; pointsAwarded: number; message: string }> {
  const response = await apiClient.post<{
    finished: boolean;
    actionKey: string;
    pointsAwarded: number;
    message: string;
  }>(`/rewards/earning-actions/${actionKey}/complete`);
  return response.data;
}

export async function getAllEarningActions(): Promise<GetEarningActionsResponse> {
  const response = await apiClient.get<GetEarningActionsResponse>(
    '/rewards/earning-actions/all'
  );
  return response.data;
}

export async function createEarningAction(
  request: CreateEarningActionRequest
): Promise<{ earningAction: EarningAction; message: string }> {
  const response = await apiClient.post<{
    earningAction: EarningAction;
    message: string;
  }>('/rewards/earning-actions', request);
  return response.data;
}

export async function updateEarningAction(
  id: string,
  request: UpdateEarningActionRequest
): Promise<{ earningAction: EarningAction; message: string }> {
  const response = await apiClient.patch<{
    earningAction: EarningAction;
    message: string;
  }>(`/rewards/earning-actions/${id}`, request);
  return response.data;
}

export async function deleteEarningAction(
  id: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/rewards/earning-actions/${id}`
  );
  return response.data;
}

// Reward History API
export interface RewardTransaction {
  id: string;
  type: 'earned' | 'redeemed' | 'claimed' | 'transferred';
  actionKey?: string;
  description: string;
  points: number;
  transactionSignature?: string;
  createdAt: string;
}

export interface GetRewardHistoryResponse {
  transactions: RewardTransaction[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export async function getRewardHistory(
  limit?: number,
  offset?: number
): Promise<GetRewardHistoryResponse> {
  const params: { limit?: string; offset?: string } = {};
  if (limit !== undefined) params.limit = limit.toString();
  if (offset !== undefined) params.offset = offset.toString();

  const response = await apiClient.get<GetRewardHistoryResponse>(
    '/rewards/history',
    { params }
  );
  return response.data;
}

// Redeem Rewards API (WebApp – synced from Admin)
export interface RedeemRewardItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  petPointsRequired: number;
  totalCap: number | null;
  perUserCap: number | null;
  redirectLink: string | null;
  expiryDate: string | null;
  sortOrder: number;
  remainingQuantity: number | null;
  totalRedeemed: number;
  discountCode?: string | null;
  discountPercent?: number | null;
  /** API may return snake_case */
  discount_code?: string | null;
  discount_percent?: number | null;
}

export interface GetRedeemRewardsResponse {
  redeemRewards: RedeemRewardItem[];
  total: number;
}

export async function getRedeemRewards(): Promise<GetRedeemRewardsResponse> {
  const response = await apiClient.get<GetRedeemRewardsResponse>(
    '/rewards/redeem-rewards'
  );
  return response.data;
}

export interface RedeemRewardRequest {
  rewardId: string;
}

export interface RedeemRewardResponse {
  success: boolean;
  rewardId: string;
  rewardTitle: string;
  pointsSpent: number;
  redirectLink?: string;
  message: string;
}

export async function redeemReward(
  request: RedeemRewardRequest
): Promise<RedeemRewardResponse> {
  const response = await apiClient.post<RedeemRewardResponse>(
    '/rewards/redeem',
    request
  );
  return response.data;
}

// Community tasks (synced from Admin → Rewards → Community Rewards)
export interface CommunityTaskApi {
  id: string;
  title: string;
  description: string;
  category: 'social-follow' | 'social-engagement' | 'referral' | 'community';
  platform?: string;
  url?: string;
  reward: number;
  enabled: boolean;
  verificationMethod: 'auto' | 'manual';
  maxParticipants?: number;
  currentParticipants: number;
  startDate?: string;
  endDate?: string;
  targetAudience: string;
  limit: string;
  active: boolean;
  finished: boolean;
  submitted: boolean;
}

export interface GetCommunityTasksResponse {
  tasks: CommunityTaskApi[];
  available: number;
  completed: CommunityTaskApi[];
  points: number;
}

export async function getCommunityTasks(): Promise<GetCommunityTasksResponse> {
  const response = await apiClient.get<GetCommunityTasksResponse>(
    '/rewards/community-tasks'
  );
  return response.data;
}

// Submit a community task for manual verification (user clicks "Submit for Verification")
export interface SubmitCommunityTaskRequest {
  taskId: string;
  taskTitle: string;
  reward: number;
  evidence?: string;
}

export interface SubmitCommunityTaskResponse {
  submission?: {
    id: string;
    taskId: string;
    status: string;
    submittedAt: string;
  };
  error?: string;
}

export async function submitCommunityTask(
  request: SubmitCommunityTaskRequest
): Promise<SubmitCommunityTaskResponse> {
  const response = await apiClient.post<SubmitCommunityTaskResponse>(
    '/rewards/community-tasks/submit',
    request
  );
  return response.data;
}

// Current user's submission statuses by taskId (to show pending/approved/rejected from server)
export interface MyCommunityTaskStatusesResponse {
  byTaskId: Record<string, { id: string; status: string; submittedAt: string }>;
  error?: string;
}

export async function getMyCommunityTaskStatuses(): Promise<MyCommunityTaskStatusesResponse> {
  const response = await apiClient.get<MyCommunityTaskStatusesResponse>(
    '/rewards/community-tasks/my-status'
  );
  return response.data;
}
